import { Card, Text } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { memo, useMemo } from "react";
import { Link } from "react-router";
import { getMinimalMRTOptions } from "../../../../mantine/getMinimalMRTOptions";
import { COLUMN_LABELS, TOOL_LABELS } from "../ToolResultRenderer.constants";
import type { ToolResultTable } from "../ToolResultRenderer.types";
import { formatCellValue } from "../utils/formatCellValue";
import { resolveLink } from "../utils/resolveLink";

interface TableCardProps {
  data: ToolResultTable;
}

function TableCardComponent({ data }: TableCardProps) {
  const columns = useMemo<Array<MRT_ColumnDef<Record<string, unknown>>>>(
    () =>
      data.columns.map((col) => ({
        id: col,
        accessorFn: (row) => row[col],
        header: COLUMN_LABELS[col] ?? col,
        Cell: ({ cell }) => {
          const value = formatCellValue(cell.getValue());

          if (col === "name") {
            const link = resolveLink(cell.row.original["_link"]);
            if (link !== null) {
              return <Link to={link}>{value}</Link>;
            }
          }

          return value;
        }
      })),
    [data.columns]
  );

  const table = useMantineReactTable({
    ...getMinimalMRTOptions<Record<string, unknown>>(),
    columns,
    data: data.rows,
    getRowId: (_row, index) => String(index)
  });

  if (data.rows.length === 0) {
    return (
      <Text c="dimmed" fs="italic" size="sm">
        Keine Ergebnisse gefunden.
      </Text>
    );
  }

  return (
    <Card padding="xs" withBorder>
      <Text fw={600} mb="xs" size="sm">
        {TOOL_LABELS[data.tool_name] ?? data.tool_name} ({data.rows.length})
      </Text>
      <MantineReactTable table={table} />
    </Card>
  );
}

const TableCard = memo(TableCardComponent);

export { TableCard };
